import React from "react"
import { makeStyles, Typography } from "@material-ui/core"
import ChartCard from "../../molecules/charts/ChartCard"
import BarChart from "../../molecules/charts/BarChart"
import { colors } from "../../../utils/theme"
import { getWeekdayAbbrev } from "../../../utils/dateTimeUtils"
import { ClientFragment } from "../../../generated/graphql"
import ChartAlert from "../../molecules/charts/ChartAlert"
import {
  getMoodChartIndexDate,
  getReportedMoodDays,
  getMoodTrend,
} from "../../../utils/moodChart"

type CravingsChartDatum = {
  dateKey: string
  cravings: number | null
}

/**
 * Displays the client's cravings ratings for the 7 reported days ending
 * yesterday.
 *
 * Mood measurements in this chart intentionally follow the existing mood-balance
 * convention from `getReportedMoodDays`: a measurement created today is treated
 * as the client's report for yesterday, so the chart excludes the current
 * calendar day and instead buckets that response under yesterday.
 *
 * Stored cravings values are zero-indexed in the database, but the chart renders
 * a 1-5 scale for people reading it. `getReportedMoodDays` already applies that
 * shift, so a stored value of `0` is displayed as `1` on the graph rather than as
 * an empty or invalid-looking lowest bucket.
 *
 * @param {ClientFragment["mood_measurements"]} data Client mood measurements used
 * to build the previous-week craving bars and week-over-week trend alert.
 */
export default function CravingsChart({
  data,
  weekOffset = 0,
}: {
  data: ClientFragment["mood_measurements"]
  weekOffset?: number
}) {
  const styles = useStyles()
  const chartIndexDate = getMoodChartIndexDate(weekOffset)
  const trailingMoodDays = getReportedMoodDays(chartIndexDate, 14, data)

  const chartData = trailingMoodDays.slice(7).map(
    (datum): CravingsChartDatum => ({
      dateKey: datum.date.format("YYYY-MM-DD"),
      cravings: datum.cravings ?? null,
    })
  )

  const thisWeekCravings = trailingMoodDays
    .slice(7)
    .map((datum) => datum.cravings)
    .filter((value): value is number => typeof value === "number")

  const previousWeekCravings = trailingMoodDays
    .slice(0, 7)
    .map((datum) => datum.cravings)
    .filter((value): value is number => typeof value === "number")

  // Calculates whether cravings increased, decreased, or stayed the same versus last week
  const cravingsTrend = getMoodTrend(thisWeekCravings, previousWeekCravings)

  const intensityColors = [
    colors.graphs.cravings.intensity100,
    colors.graphs.cravings.intensity200,
    colors.graphs.cravings.intensity300,
    colors.graphs.cravings.intensity400,
    colors.graphs.cravings.intensity500,
  ]

  const getIntensityColor = (datum: CravingsChartDatum) => {
    const intensity = datum.cravings ?? 0
    const index = Math.min(
      Math.max(intensity - 1, 0),
      intensityColors.length - 1
    )

    return intensityColors[index]
  }

  const legendComponent = (): React.ReactNode => {
    return (
      <div className={styles.legend}>
        <Typography className={styles.legend}>1 = Not at all</Typography>
        <Typography className={styles.legend}>2 = A little</Typography>
        <Typography className={styles.legend}>3 = Moderate</Typography>
        <Typography className={styles.legend}>4 = Quite a bit</Typography>
        <Typography className={styles.legend}>5 = Extreme</Typography>
      </div>
    )
  }

  const alertComponent = (): React.ReactNode => {
    if (!cravingsTrend || cravingsTrend.direction === "no_change") {
      return null
    }

    const isCravingsDecreasing = cravingsTrend.direction === "decreasing"

    return (
      <ChartAlert
        association={isCravingsDecreasing ? "positive" : "negative"}
        variant={cravingsTrend.direction}
        changeAmount={Number(cravingsTrend.meanChange.toFixed(1))}
        message="cravings score this week"
      />
    )
  }

  const xTickFormatter = (tick: string) => {
    return getWeekdayAbbrev(tick)
  }

  return (
    <ChartCard title="Cravings">
      <BarChart
        data={chartData}
        xKey={"dateKey"}
        yKey={"cravings"}
        maxY={5}
        yTicks={[1, 3, 5]}
        yTickFormatter={(value) => value.toString()}
        xTickFormatter={xTickFormatter}
        getBarColor={(d) => getIntensityColor(d)}
        height={135}
        width={420}
      />

      <div className={styles.footer}>
        <div className={styles.legendContainer}>{legendComponent()}</div>
        {alertComponent() ? (
          <div className={styles.alert}>{alertComponent()}</div>
        ) : null}
      </div>
    </ChartCard>
  )
}

const useStyles = makeStyles({
  footer: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    marginTop: 8,
    flexWrap: "nowrap",
  },
  legend: {
    color: colors.safetyPlan.graphText,
    fontSize: "12px",
  },
  legendContainer: {
    flex: "1 1 auto",
    minWidth: 0,
    padding: 10,
  },
  alert: {
    flex: "0 0 auto",
    marginLeft: "auto",
  },
})
